import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useMobile } from '../../contexts/MobileContext';
import { useAccountant } from '../../contexts/AccountantContext';
import { 
  House, 
  Users, 
  CurrencyDollar, 
  ChartLineUp, 
  Gear,
  CaretDoubleLeft,
  CaretDoubleRight,
  UsersThree,
  Shield,
  ShieldWarning,
  Signature,
  Buildings,
  Money,
  ShoppingCart,
  Stamp,
  Bell,
  ChartPieSlice,
  TrendUp,
  Lightbulb,
  Wallet,
  CaretDown,
  Check,
  Plus,
  X,
  ShieldCheck,
  HardDrive,
  CalendarBlank,
  Package,
  Wrench,
  Handshake,
  ClockCounterClockwise,
  ClockUser,
  Kanban,
  ChatCircle,
  Question,
  Receipt,
  Book,
  Lock,
  Trash,
  Crown,
  Briefcase,
  Archive,
  BookOpen,
  Notepad,
  FolderOpen,
  Eye,
  CreditCard,
  FileText,
  Storefront,
  Note as StickyNote,
  Megaphone
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const { logoUrl, company, refreshCompany } = useCompany();
  const { isMobile, sidebarOpen, closeSidebar, sidebarCollapsed: collapsed, toggleSidebarCollapsed } = useMobile();
  const { accountantMode } = useAccountant();
  const setCollapsed = (next) => {
    // Backwards-compat helper; supports both setCollapsed(bool) and the
    // previous setCollapsed(prev => !prev) callback shape.
    if (typeof next === 'function') {
      // Resolve against the current value so the toggle still works.
      if (next(collapsed) !== collapsed) toggleSidebarCollapsed();
    } else if (next !== collapsed) {
      toggleSidebarCollapsed();
    }
  };
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [userCompanies, setUserCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Fetch user's companies for multi-company support
  useEffect(() => {
    const fetchUserCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const response = await axios.get(`${API_URL}/api/user/companies`);
        setUserCompanies(response.data || []);
      } catch (error) {
        // If endpoint doesn't exist yet, use current company
        console.log('Failed to fetch companies:', error);
        if (company) {
          setUserCompanies([company]);
        }
      } finally {
        setLoadingCompanies(false);
      }
    };
    
    // Only fetch if multi-company is enabled
    if (user && company?.multi_company_enabled) {
      fetchUserCompanies();
    } else if (company) {
      setUserCompanies([company]);
    }
  }, [user, company]);

  const switchCompany = async (companyId) => {
    try {
      await axios.post(`${API_URL}/api/user/switch-company`, { company_id: companyId });
      refreshCompany();
      setCompanySwitcherOpen(false);
      window.location.reload(); // Reload to refresh all data
    } catch (error) {
      console.error('Failed to switch company:', error);
    }
  };

  // Check permissions
  const canViewUsers = user?.permissions?.users?.includes('view');
  const canViewRoles = user?.permissions?.roles?.includes('view');
  const canViewSettings = user?.permissions?.settings?.includes('view');

  const isPlatformAdmin = user?.email?.toLowerCase() === 'sgkapps.in@gmail.com' || user?.is_platform_admin;
  const [viewAsUser, setViewAsUser] = useState(false);

  const navItems = [
    {
      label: 'Platform Admin',
      path: '/dashboard/platform-admin',
      icon: ShieldCheck,
      description: 'Manage all tenant companies, users, subscriptions',
      show: isPlatformAdmin && !viewAsUser
    },
    { 
      label: 'Dashboard', 
      path: '/dashboard', 
      icon: House,
      // Hide tenant operational dashboard for platform admins unless viewing-as-user
      show: !isPlatformAdmin || viewAsUser
    },
    // Accountant Dashboard - Only shows when Accountant Mode is enabled
    { 
      label: 'Accountant Hub', 
      path: '/dashboard/accountant', 
      icon: Briefcase,
      description: 'Multi-Client Management',
      show: accountantMode
    },
    {
      label: 'Reports',
      path: '/dashboard/reports',
      icon: Notepad,
      description: 'Prepare Business Reports',
      show: !isPlatformAdmin || viewAsUser
    },
    { 
      label: 'HR', 
      path: '/dashboard/hr', 
      icon: Users,
      description: 'Human Resources',
      show: user?.permissions?.hr?.includes('view')
    },
    { 
      label: 'Payroll', 
      path: '/dashboard/payroll', 
      icon: Money,
      description: 'Payroll Management',
      show: user?.permissions?.hr?.includes('view')
    },
    { 
      label: 'Attendance', 
      path: '/dashboard/attendance', 
      icon: ClockUser,
      description: 'Attendance & Leave',
      show: user?.permissions?.hr?.includes('view')
    },
    { 
      label: 'Compliance', 
      path: '/dashboard/compliance', 
      icon: ShieldCheck,
      description: 'Compliance Center',
      show: user?.permissions?.hr?.includes('view')
    },
    { 
      label: 'Purchases', 
      path: '/dashboard/purchases', 
      icon: ShoppingCart,
      description: 'Purchase Management',
      show: user?.permissions?.hr?.includes('view')
    },
    { 
      label: 'Approvals', 
      path: '/dashboard/approvals', 
      icon: Stamp,
      description: 'Approval Workflows',
      show: user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager'
    },
    { 
      label: 'Accounts', 
      path: '/dashboard/accounts', 
      icon: CurrencyDollar,
      description: 'Finance & Accounting',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Sales', 
      path: '/dashboard/sales', 
      icon: ChartLineUp,
      description: 'Sales & Invoicing',
      show: user?.permissions?.sales?.includes('view')
    },
    { 
      label: 'Credit / Debit Notes', 
      path: '/dashboard/notes', 
      icon: Receipt,
      description: 'Post-invoice adjustments',
      show: user?.permissions?.sales?.includes('view') || user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Clients', 
      path: '/dashboard/contacts?type=customer', 
      icon: Users,
      description: 'Customer directory',
      show: user?.permissions?.sales?.includes('view') || user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Suppliers', 
      path: '/dashboard/contacts?type=supplier', 
      icon: Storefront,
      description: 'Supplier directory',
      show: user?.permissions?.sales?.includes('view') || user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Analytics', 
      path: '/dashboard/analytics', 
      icon: ChartPieSlice,
      description: 'Analytics Dashboard',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Forecasting', 
      path: '/dashboard/forecasting', 
      icon: TrendUp,
      description: 'Financial Forecasting',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Insights', 
      path: '/dashboard/insights', 
      icon: Lightbulb,
      description: 'Smart Insights',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Cost Control', 
      path: '/dashboard/cost-control', 
      icon: Wallet,
      description: 'Budget & Cost Management',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Financial Reports', 
      path: '/dashboard/accounts/reports', 
      icon: ChartPieSlice,
      description: 'P&L & Balance Sheet',
      show: user?.permissions?.accounts?.includes('view')
    },
    {
      label: 'Statement of Accounts',
      path: '/dashboard/accounts/statements',
      icon: ChartPieSlice,
      description: 'Receivables & Payables — per client, aging',
      show: user?.permissions?.accounts?.includes('view') || user?.permissions?.sales?.includes('view')
    },
    {
      label: 'Bank Reconciliation',
      path: '/dashboard/accounts/bank-reconciliation',
      icon: Wallet,
      description: 'Upload bank statement, find voucher differences',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Tax Compliance', 
      path: '/dashboard/accounts/tax', 
      icon: Receipt,
      description: 'UAE VAT & Corporate Tax',
      show: user?.permissions?.accounts?.includes('view')
    },
    {
      label: 'VAT Invoice Summary',
      path: '/dashboard/reports/vat-invoice-summary',
      icon: Receipt,
      description: 'Editable quarterly tax-invoice summary',
      show: user?.permissions?.accounts?.includes('view') || user?.permissions?.sales?.includes('view')
    },
    { 
      label: 'Vouchers', 
      path: '/dashboard/vouchers', 
      icon: Receipt,
      description: 'Payment & Receipt Vouchers',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Journal Entries', 
      path: '/dashboard/accounts/journal', 
      icon: FileText,
      description: 'Manual journal entries (income/expense/adjustments)',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'General Ledger', 
      path: '/dashboard/accounts/ledger', 
      icon: BookOpen,
      description: 'Account-level transaction history with running balance',
      show: user?.permissions?.accounts?.includes('view')
    },
    { 
      label: 'Audit Trail', 
      path: '/dashboard/accounts/audit-trail', 
      icon: ClockCounterClockwise,
      description: 'Complete history of every change',
      show: ['admin', 'super_admin'].includes(user?.role)
    },
    { 
      label: 'Period Locks', 
      path: '/dashboard/accounts/period-locks', 
      icon: Lock,
      description: 'Close accounting periods',
      show: ['admin', 'super_admin'].includes(user?.role)
    },
    { 
      label: 'Storage', 
      path: '/dashboard/storage', 
      icon: HardDrive,
      description: 'Document Storage',
      show: user?.permissions?.hr?.includes('view')
    },
    { 
      label: 'Calendar', 
      path: '/dashboard/calendar', 
      icon: CalendarBlank,
      description: 'Calendar & Reminders',
      show: user?.permissions?.hr?.includes('view')
    },
    { 
      label: 'Inventory', 
      path: '/dashboard/inventory', 
      icon: Package,
      description: 'Stock & Inventory',
      show: user?.permissions?.hr?.includes('view')
    },
    { 
      label: 'Job Orders', 
      path: '/dashboard/job-orders', 
      icon: Wrench,
      description: 'Field work orders, PPM & service reports',
      show: true
    },
    { 
      label: 'AMC', 
      path: '/dashboard/agreements', 
      icon: Handshake,
      description: 'Annual Maintenance Contracts, service SLAs',
      show: true
    },
    { 
      label: 'Subcontractor Agreements', 
      path: '/dashboard/subcontractor-agreements', 
      icon: Handshake,
      description: 'Branded subcontractor & supplier agreements',
      show: true
    },
    { 
      label: 'Tasks', 
      path: '/dashboard/tasks', 
      icon: Kanban,
      description: 'Task Management',
      show: true
    },
    { 
      label: 'Chat', 
      path: '/dashboard/chat', 
      icon: ChatCircle,
      description: 'Team Messages',
      show: true
    },
    { 
      label: 'Notes', 
      path: '/dashboard/notes', 
      icon: StickyNote,
      description: 'Personal scratchpad',
      show: true
    },
  ];

  const adminItems = [
    { 
      label: 'Users', 
      path: '/dashboard/users', 
      icon: UsersThree,
      show: canViewUsers
    },
    { 
      label: 'Roles', 
      path: '/dashboard/roles', 
      icon: Shield,
      show: canViewRoles
    },
    {
      label: 'My Profile',
      path: '/dashboard/profile',
      icon: Signature,
      description: 'Signature & display name',
      show: true,
    },
    { 
      label: 'Company', 
      path: '/dashboard/company', 
      icon: Buildings,
      show: canViewSettings
    },
    { 
      label: 'Billing', 
      path: '/dashboard/billing', 
      icon: CreditCard,
      description: 'Subscription & payments',
      show: user?.role === 'admin' || user?.role === 'super_admin'
    },
    { 
      label: 'Audit Logs', 
      path: '/dashboard/audit', 
      icon: ClockCounterClockwise,
      show: user?.role === 'admin' || user?.role === 'super_admin'
    },
    { 
      label: 'Backup & Recovery', 
      path: '/dashboard/backup', 
      icon: Archive,
      description: 'Create backups & restore data',
      show: user?.role === 'admin' || user?.role === 'super_admin'
    },
    { 
      label: 'Security', 
      path: '/dashboard/security', 
      icon: Lock,
      description: 'Security & Access Control',
      show: user?.role === 'admin' || user?.role === 'super_admin'
    },
    {
      label: 'Recycle Bin',
      path: '/dashboard/recycle-bin',
      icon: Trash,
      description: 'Deleted Documents',
      show: user?.role === 'admin' || user?.role === 'super_admin'
    },
    { 
      label: 'Deleted Files', 
      path: '/dashboard/deleted-documents', 
      icon: Trash,
      description: 'Restore or permanently delete',
      show: user?.role === 'admin' || user?.role === 'super_admin'
    },
    { 
      label: 'Platform Admin', 
      path: '/dashboard/platform-admin', 
      icon: Crown,
      description: 'Manage all users & subscriptions',
      show: user?.email?.toLowerCase() === 'sgkapps.in@gmail.com' || user?.is_platform_admin
    },
  ];

  const bottomItems = [
    { 
      label: 'User Guide', 
      path: '/dashboard/guide', 
      icon: Book,
      show: true
    },
    { 
      label: 'App Booklet', 
      path: '/dashboard/booklet', 
      icon: BookOpen,
      show: true
    },
    { 
      label: 'Help & FAQ', 
      path: '/dashboard/help', 
      icon: Question,
      show: true
    },
    // Marketing / social-post generator — public route, opens in a new
    // tab so the user's dashboard session stays intact while they
    // preview and download the shareable creatives.
    {
      label: 'Social Post',
      path: '/marketing/social-post',
      icon: Megaphone,
      external: true,
      badge: 'New',
      show: true,
    },
    { 
      label: 'Settings', 
      path: '/dashboard/settings', 
      icon: Gear,
      // Gate on RBAC — hides the entry entirely when Company Admin has
      // revoked settings.view for the caller's role. Prevents the "click →
      // Access Denied" leak of the nav affordance itself.
      show: canViewSettings
    },
  ];

  const NavItem = ({ item }) => {
    if (!item.show) return null;
    
    const Icon = item.icon;
    // Support paths with query params (e.g. /dashboard/contacts?type=customer)
    const [itemPath, itemQuery] = item.path.split('?');
    const currentSearch = (location.search || '').replace(/^\?/, '');
    const isActive = location.pathname === itemPath && (
      itemQuery ? currentSearch.includes(itemQuery) : true
    );

    // External / new-tab targets — used for public marketing pages so the
    // user doesn't lose their dashboard context on click.
    if (item.external) {
      return (
        <a
          href={item.path}
          target="_blank"
          rel="noreferrer"
          onClick={() => isMobile && closeSidebar()}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
            "hover:bg-accent group",
            collapsed && "justify-center px-2"
          )}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
        >
          <Icon size={20} weight="regular" className="flex-shrink-0 text-muted-foreground group-hover:text-foreground" />
          {!collapsed && (
            <span className="text-sm text-muted-foreground group-hover:text-foreground flex-1">{item.label}</span>
          )}
          {!collapsed && item.badge && (
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600">
              {item.badge}
            </span>
          )}
        </a>
      );
    }
    
    return (
      <NavLink
        to={item.path}
        onClick={() => isMobile && closeSidebar()}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
          "hover:bg-accent group",
          isActive && "bg-primary/10 text-primary font-medium",
          collapsed && "justify-center px-2"
        )}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Icon 
          size={20} 
          weight={isActive ? "fill" : "regular"}
          className={cn(
            "flex-shrink-0",
            isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
          )}
        />
        {!collapsed && (
          <span className={cn(
            "text-sm",
            isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
          )}>
            {item.label}
          </span>
        )}
      </NavLink>
    );
  };

  // Don't render sidebar on mobile when closed
  if (isMobile && !sidebarOpen) {
    return null;
  }

  return (
    <aside 
      className={cn(
        "fixed top-0 left-0 h-full bg-card border-r border-border flex flex-col z-50",
        "transition-all duration-300",
        isMobile ? "w-72" : (collapsed ? "w-20" : "w-64")
      )}
      data-testid="sidebar"
    >
      {/* Logo & Company Switcher */}
      <div className={cn(
        "h-16 flex items-center border-b border-border px-4",
        collapsed && !isMobile ? "justify-center" : "justify-between"
      )}>
        <div 
          className="flex items-center gap-2 cursor-pointer flex-1 min-w-0"
          onClick={() => userCompanies.length > 1 && setCompanySwitcherOpen(!companySwitcherOpen)}
        >
          {logoUrl ? (
            <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
              <img 
                src={logoUrl} 
                alt={company?.name || 'Company'} 
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
              <span className="text-primary-foreground font-heading font-bold text-base">
                {company?.name?.[0] || 'E'}
              </span>
            </div>
          )}
          {(!collapsed || isMobile) && (
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <span className="font-heading font-semibold text-base truncate">
                {company?.name || 'ERP Hub'}
              </span>
              {userCompanies.length > 1 && (
                <CaretDown 
                  size={14} 
                  className={cn(
                    "text-muted-foreground flex-shrink-0 transition-transform",
                    companySwitcherOpen && "rotate-180"
                  )} 
                />
              )}
            </div>
          )}
        </div>
        
        {/* Close button on mobile, collapse on desktop */}
        {isMobile ? (
          <button
            onClick={closeSidebar}
            className="p-1.5 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
            data-testid="sidebar-close"
          >
            <X size={20} weight="bold" />
          </button>
        ) : (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "p-1.5 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground",
              collapsed && "absolute -right-3 top-5 bg-background border border-border shadow-sm"
            )}
            data-testid="sidebar-toggle"
          >
            {collapsed ? (
              <CaretDoubleRight size={16} weight="bold" />
            ) : (
              <CaretDoubleLeft size={16} weight="bold" />
            )}
          </button>
        )}
      </div>

      {/* Company Switcher Dropdown */}
      {companySwitcherOpen && userCompanies.length > 1 && (!collapsed || isMobile) && (
        <div className="border-b border-border bg-muted/30 py-2 px-2">
          <p className="text-xs text-muted-foreground px-2 mb-2">Switch Company</p>
          {userCompanies.map((c) => (
            <button
              key={c.id}
              onClick={() => switchCompany(c.id)}
              className={cn(
                "w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors",
                c.id === company?.id 
                  ? "bg-primary/10 text-primary" 
                  : "hover:bg-accent text-foreground"
              )}
            >
              <div className="w-6 h-6 rounded bg-muted flex items-center justify-center text-xs font-medium">
                {c.name?.[0] || 'C'}
              </div>
              <span className="truncate flex-1 text-left">{c.name}</span>
              {c.id === company?.id && <Check size={16} className="text-primary" />}
            </button>
          ))}
          <button
            className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground mt-1"
            onClick={() => {/* TODO: Create new company */}}
          >
            <Plus size={16} />
            <span>Add Company</span>
          </button>
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">

        {/* PLATFORM ADMIN VIEW */}
        {isPlatformAdmin && !viewAsUser ? (
          <>
            {/* Platform Admin Tools */}
            <div className={cn(
              "text-xs font-semibold uppercase tracking-wider mb-2",
              collapsed && !isMobile ? "text-center text-muted-foreground" : "px-3 text-amber-600 dark:text-amber-400"
            )}>
              {collapsed && !isMobile ? "•" : "Platform Admin"}
            </div>
            {navItems.filter(item => ['Dashboard'].includes(item.label)).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
            <NavItem item={{ label: 'Platform Admin', path: '/dashboard/platform-admin', icon: Crown, show: true }} />
            <NavItem item={{ label: 'Settings', path: '/dashboard/settings', icon: Gear, show: true }} />

            {/* View as User toggle */}
            <div className={cn("mt-5 mb-2", collapsed && !isMobile ? "text-center" : "px-3")}>
              <button
                onClick={() => setViewAsUser(true)}
                className={cn(
                  "flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  "bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20"
                )}
                data-testid="view-as-user-btn"
              >
                {collapsed && !isMobile ? <Eye size={16} /> : (
                  <>
                    <Eye size={16} />
                    <span>View as User</span>
                    <FolderOpen size={14} className="ml-auto opacity-60" />
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* REGULAR USER VIEW (or Platform Admin in "View as User" mode) */}
            {isPlatformAdmin && viewAsUser && (
              <div className={cn("mb-3", collapsed && !isMobile ? "text-center" : "px-3")}>
                <button
                  onClick={() => setViewAsUser(false)}
                  className={cn(
                    "flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs font-semibold transition-colors",
                    "bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 border border-amber-500/20"
                  )}
                  data-testid="exit-view-as-user-btn"
                >
                  {collapsed && !isMobile ? <Crown size={14} /> : (
                    <>
                      <Eye size={14} />
                      <span>Viewing as User</span>
                      <span className="ml-auto text-[10px] opacity-70">Exit</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Operations Category */}
            <div className={cn(
              "text-xs font-semibold uppercase tracking-wider mb-2",
              collapsed && !isMobile ? "text-center text-muted-foreground" : "px-3 text-blue-600 dark:text-blue-400"
            )}>
              {collapsed && !isMobile ? "•" : "Operations"}
            </div>
            {navItems.filter(item => ['Dashboard', 'Accountant Hub', 'Reports'].includes(item.label)).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}

            {/* People & Workforce */}
            <div className={cn(
              "text-xs font-semibold uppercase tracking-wider mt-5 mb-2",
              collapsed && !isMobile ? "text-center text-muted-foreground" : "px-3 text-emerald-600 dark:text-emerald-400"
            )}>
              {collapsed && !isMobile ? "•" : "People & Workforce"}
            </div>
            {navItems.filter(item => ['HR', 'Payroll', 'Attendance', 'Compliance'].includes(item.label)).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}

            {/* Trade & Commerce */}
            <div className={cn(
              "text-xs font-semibold uppercase tracking-wider mt-5 mb-2",
              collapsed && !isMobile ? "text-center text-muted-foreground" : "px-3 text-orange-600 dark:text-orange-400"
            )}>
              {collapsed && !isMobile ? "•" : "Trade & Commerce"}
            </div>
            {navItems.filter(item => ['Purchases', 'Suppliers', 'Sales', 'Credit / Debit Notes', 'Clients', 'Approvals', 'Inventory', 'Job Orders', 'AMC', 'Subcontractor Agreements'].includes(item.label)).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}

            {/* Finance & Accounts */}
            <div className={cn(
              "text-xs font-semibold uppercase tracking-wider mt-5 mb-2",
              collapsed && !isMobile ? "text-center text-muted-foreground" : "px-3 text-violet-600 dark:text-violet-400"
            )}>
              {collapsed && !isMobile ? "•" : "Finance & Accounts"}
            </div>
            {navItems.filter(item => ['Accounts', 'Journal Entries', 'Financial Reports', 'Statement of Accounts', 'Bank Reconciliation', 'Tax Compliance', 'VAT Invoice Summary', 'Vouchers'].includes(item.label)).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}

            {/* Analytics & Insights */}
            <div className={cn(
              "text-xs font-semibold uppercase tracking-wider mt-5 mb-2",
              collapsed && !isMobile ? "text-center text-muted-foreground" : "px-3 text-cyan-600 dark:text-cyan-400"
            )}>
              {collapsed && !isMobile ? "•" : "Analytics & Insights"}
            </div>
            {navItems.filter(item => ['Analytics', 'Forecasting', 'Insights', 'Cost Control'].includes(item.label)).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}

            {/* Productivity */}
            <div className={cn(
              "text-xs font-semibold uppercase tracking-wider mt-5 mb-2",
              collapsed && !isMobile ? "text-center text-muted-foreground" : "px-3 text-amber-600 dark:text-amber-400"
            )}>
              {collapsed && !isMobile ? "•" : "Productivity"}
            </div>
            {navItems.filter(item => ['Storage', 'Calendar', 'Tasks', 'Chat', 'Notes'].includes(item.label)).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
            
            {/* Admin Section */}
            {(canViewUsers || canViewRoles || canViewSettings) && (
              <>
                <div className={cn(
                  "text-xs font-semibold uppercase tracking-wider mt-5 mb-2",
                  collapsed && !isMobile ? "text-center text-muted-foreground" : "px-3 text-rose-600 dark:text-rose-400"
                )}>
                  {collapsed && !isMobile ? "•" : "Administration"}
                </div>
                {adminItems.map((item) => (
                  <NavItem key={item.path} item={item} />
                ))}
              </>
            )}
          </>
        )}
      </nav>

      {/* Bottom Navigation */}
      <div className="border-t border-border p-3 space-y-1">
        {bottomItems.map((item) => (
          <NavItem key={item.path} item={item} />
        ))}
      </div>
    </aside>
  );
}
